/* DARMA launch navigation — desktop editorial panel (direction B).
   Opens the one wide panel on pointer or keyboard focus, swaps the two doors per
   category, keeps aria truthful, closes on ESC / pointer-leave / focus-leave /
   SCROLL / any pointer entering the veil.
   No dependencies, guarded, safe to load twice. The mobile drawer is Dawn's
   <header-drawer> details/summary component and needs none of this.

   BUG FIX, 20 Sept 2026 (Derek: hovering a bar item opens the panel and it never
   closes; scrolling down does not dismiss it, only going back above the toolbar
   does). AUDIT.md §4 measured two causes, both fixed here because CSS cannot fix
   either — the panel and the veil are descendants of the element that owns the
   hover:
     1. `.darma-nav__veil` is `height:100vh` with `pointer-events:auto` while open
        and it is a CHILD of <nav class="darma-nav">, the only element with a
        `mouseleave` close handler. `mouseleave` fires only when the pointer
        leaves the element AND every descendant, so the whole viewport below the
        header still counted as hovering the nav. The veil now closes the panel
        itself on `mouseenter` and on `click`.
     2. There was no `scroll` handler while <sticky-header> slides the panel under
        a stationary cursor (the measured `elementFromPoint` after a scroll was
        `div.darma-nav__panel`, so the hover was actively re-asserted). A passive
        `scroll` listener now closes, but only once the page has actually moved
        from the scroll position the panel opened at, so that opening the panel
        cannot be undone by the same frame's scroll restoration.
     3. Secondary: `closeSoon()` refused to close while anything inside the nav
        held focus, so a keyboard user who then moved the pointer away kept the
        panel open. It now closes when the pointer leaves unless the PANEL holds
        that focus; `close()` itself is unconditional.
     4. Secondary, found in verification: ESC pressed from inside the panel called
        `close()` and then moved focus back to the first bar link — whose `focus`
        handler opened the panel again in the same tick, so ESC appeared to do
        nothing. The restore is now guarded, so ESC always leaves it closed.

   2 Oct 2026 (Derek: the panel only closes once the mouse is far down the page).
   The root <nav> spans the whole bar row, so the pointer resting anywhere in that
   row kept the panel open. While open, a passive `mousemove` now checks the
   pointer against the live zone only: the bar list, the strip under it down to
   the panel, and the panel. Outside it the panel closes after a 120ms intent
   delay. ESC is heard on the document, so it also closes a panel opened by hover. */
(function () {
  if (window.__darmaNav) return;
  window.__darmaNav = true;

  var DESKTOP = '(min-width: 990px)';

  function init(root) {
    if (!root || root.dataset.darmaNavReady === 'true') return;
    root.dataset.darmaNavReady = 'true';

    var panel = root.querySelector('[data-darma-nav-panel]');
    var bar = Array.prototype.slice.call(root.querySelectorAll('[data-darma-nav-link]'));
    var cats = Array.prototype.slice.call(root.querySelectorAll('[data-darma-nav-cat]'));
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-darma-nav-tiles]'));
    if (!panel || !bar.length) return;

    var isDesktop = function () {
      return window.matchMedia(DESKTOP).matches;
    };
    var closeTimer = null;
    var barList = root.querySelector('.darma-nav__bar');
    var openedAtY = 0;
    /* true only while close() is restoring focus to the bar after ESC, so that
       restore cannot re-trigger open() through the bar link's focus handler */
    var restoringFocus = false;

    function setActive(key) {
      if (!key) return;
      groups.forEach(function (g) {
        g.classList.toggle('is-on', g.getAttribute('data-darma-nav-tiles') === key);
      });
      cats.forEach(function (c) {
        c.classList.toggle('is-on', c.getAttribute('data-darma-nav-cat') === key);
      });
      bar.forEach(function (b) {
        b.classList.toggle('is-on', b.getAttribute('data-darma-nav-link') === key);
      });
    }

    /* the bar list, the strip under it down to the panel's top, and the panel */
    function inZone(x, y) {
      var p = panel.getBoundingClientRect();
      if (x >= p.left && x <= p.right && y >= p.top && y <= p.bottom) return true;
      var b = barList ? barList.getBoundingClientRect() : null;
      return !!b && x >= b.left && x <= b.right && y >= b.top && y < p.top;
    }

    function onMove(event) {
      if (inZone(event.clientX, event.clientY)) {
        window.clearTimeout(closeTimer);
      } else {
        closeSoon();
      }
    }

    function open(key) {
      if (!isDesktop() || restoringFocus) return;
      window.clearTimeout(closeTimer);
      setActive(key);
      openedAtY = window.scrollY || window.pageYOffset || 0;
      if (!root.classList.contains('is-open')) {
        document.addEventListener('mousemove', onMove, { passive: true });
      }
      root.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      bar.forEach(function (b) {
        b.setAttribute('aria-expanded', 'true');
      });
    }

    function close() {
      window.clearTimeout(closeTimer);
      document.removeEventListener('mousemove', onMove, { passive: true });
      root.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      bar.forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
        b.classList.remove('is-on');
      });
    }

    function closeSoon() {
      if (!root.classList.contains('is-open')) return;
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(function () {
        /* a focused element inside the nav keeps the panel open only while the
           panel itself holds that focus; a focused BAR link must not pin it. */
        if (panel.contains(document.activeElement)) return;
        close();
      }, 120);
    }

    bar.forEach(function (link) {
      var key = link.getAttribute('data-darma-nav-link');
      link.addEventListener('mouseenter', function () {
        open(key);
      });
      link.addEventListener('focus', function () {
        open(key);
      });
    });

    cats.forEach(function (cat) {
      var key = cat.getAttribute('data-darma-nav-cat');
      cat.addEventListener('mouseenter', function () {
        open(key);
      });
      cat.addEventListener('focus', function () {
        open(key);
      });
    });

    root.addEventListener('mouseleave', closeSoon);
    root.addEventListener('mouseenter', function () {
      window.clearTimeout(closeTimer);
    });
    root.addEventListener('focusout', closeSoon);

    /* the veil is the whole viewport below the bar: entering it, or clicking it,
       is the user leaving the navigation. It is a descendant of `root`, so this
       has to be its own binding — `root`'s mouseleave never fires for it. */
    var veil = root.querySelector('.darma-nav__veil');
    if (veil) {
      veil.addEventListener('mouseenter', close);
      veil.addEventListener('click', function (event) {
        event.preventDefault();
        close();
      });
    }

    /* scrolling dismisses the panel. Passive, and gated on the page having moved
       at least 8px from where the panel opened, so a scroll-restoration frame or
       a sub-pixel jitter cannot close it in the same tick it opened. */
    window.addEventListener(
      'scroll',
      function () {
        if (!root.classList.contains('is-open')) return;
        var y = window.scrollY || window.pageYOffset || 0;
        if (Math.abs(y - openedAtY) < 8) return;
        close();
      },
      { passive: true }
    );

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!root.classList.contains('is-open')) return;
      event.stopPropagation();
      close();
      var focused = document.activeElement;
      if (focused && panel.contains(focused) && bar[0]) {
        restoringFocus = true;
        bar[0].focus();
        window.setTimeout(function () { restoringFocus = false; }, 0);
      }
    });

    window.addEventListener(
      'resize',
      function () {
        if (!isDesktop()) close();
      },
      { passive: true }
    );

    /* QA-A, 2 Oct 2026: the door photographs are lazy, so a first hover showed blank
       tiles. Fetch them once the page has loaded (desktop only), or at the first
       pointer/focus on the bar if that comes sooner. */
    var warmed = false;
    function warm() {
      if (warmed || !isDesktop()) return;
      warmed = true;
      panel.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
    }
    root.addEventListener('pointerenter', warm);
    root.addEventListener('focusin', warm);
    if (document.readyState === 'complete') window.setTimeout(warm, 1500);
    else window.addEventListener('load', function () { window.setTimeout(warm, 1500); }, { once: true });

    close();
  }

  function boot() {
    document.querySelectorAll('[data-darma-nav]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
